import { useState, useEffect, useMemo } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandGroup,
  CommandEmpty,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useCountries } from '@/contexts/country';
import type { PhoneData } from '@/types/tournament/quickRegistration';

interface PhoneInputProps {
  value: PhoneData;
  onChange: (phone: PhoneData) => void;
  onBlur?: () => void;
  error?: string;
  isRequired?: boolean;
  label?: string;
  countryCodeLabel?: string;
  placeholder?: string;
  // Admin-configured max digits (game field config); falls back to universal cap when omitted.
  maxLength?: number;
}

export const PhoneInput = ({
  value,
  onChange,
  onBlur,
  error,
  isRequired = false,
  label,
  countryCodeLabel,
  placeholder,
  maxLength,
}: PhoneInputProps) => {
  const { t: tCommon } = useTranslation('common');
  const { countryCodes, validatePhoneInput, getPhoneMaxLength } =
    useCountries();

  // Prefer the admin's field config; fall back to the universal cap when none is set.
  const effectiveMaxLength = maxLength ?? getPhoneMaxLength();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Reset search when popover closes
  useEffect(() => {
    if (!open) {
      setSearchValue('');
    }
  }, [open]);

  const finalLabel = label || tCommon('labels.phoneNumber');
  const finalCountryCodeLabel =
    countryCodeLabel || tCommon('labels.countryCode');
  const finalPlaceholder =
    placeholder || tCommon('placeholder.enterPhoneNumberPlaceholder');

  // Find current country by code and abbreviation to handle countries with same phone code (e.g., US and CA both use +1)
  const currentCountry = countryCodes.find(
    c => c.code === value.code && c.abbreviation === value.abbreviation,
  );
  const selectedCountryId = currentCountry?._id || '';
  const displayValue = currentCountry
    ? `${currentCountry.code} ${currentCountry.abbreviation}`
    : value.code;

  // Sort country codes alphabetically by abbreviation (A to Z)
  const sortedCountryCodes = useMemo(() => {
    return [...countryCodes].sort((a, b) => {
      const abbrevA = (a.abbreviation || '').trim().toUpperCase();
      const abbrevB = (b.abbreviation || '').trim().toUpperCase();

      // Handle empty abbreviations - put them at the end
      if (!abbrevA && !abbrevB) return 0;
      if (!abbrevA) return 1;
      if (!abbrevB) return -1;

      // Use localeCompare for proper alphabetical sorting
      return abbrevA.localeCompare(abbrevB);
    });
  }, [countryCodes]);

  // Create search-friendly label for each country (code, abbreviation, and name)
  const getCountryLabel = (country: (typeof countryCodes)[0]) => {
    return `${country.code} ${country.abbreviation} ${country.name || ''}`.trim();
  };

  const handleCountryChange = (selectedId: string) => {
    const selectedCountry = countryCodes.find(c => c._id === selectedId);
    if (selectedCountry) {
      onChange({
        code: selectedCountry.code,
        number: value.number,
        countryName: selectedCountry.name || null,
        abbreviation: selectedCountry.abbreviation || null,
      });
      setOpen(false);
    }
  };

  return (
    <div className='flex gap-2'>
      <div className='w-32 flex flex-col'>
        {/* Consistent label area */}
        <div className='h-5 mb-2'>
          <Label className='text-gray-300'>
            {finalCountryCodeLabel}
            {isRequired && <span className='text-red-500'> *</span>}
          </Label>
        </div>
        {/* Input area */}
        <div>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                role='combobox'
                className='w-full justify-between bg-gray-900 border-gray-700 text-white hover:bg-gray-800'
              >
                <span className='truncate'>{displayValue}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className='w-[var(--radix-popover-trigger-width)] p-0 bg-gray-800 border-gray-700'
              onWheel={e => {
                // Prevent scroll events from bubbling to parent dialog
                e.stopPropagation();
              }}
            >
              <Command
                value={
                  searchValue
                    ? undefined
                    : currentCountry
                      ? getCountryLabel(currentCountry)
                      : undefined
                }
                shouldFilter={!!searchValue}
              >
                <CommandInput
                  placeholder={tCommon(
                    'search.placeholder.country',
                    'Search country...',
                  )}
                  className='text-white placeholder-gray-400'
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandList
                  key={searchValue ? `search-${searchValue}` : `open-${open}`}
                  className='max-h-48'
                >
                  <CommandEmpty>
                    {tCommon('labels.noCountryFound')}
                  </CommandEmpty>
                  <CommandGroup>
                    {sortedCountryCodes.map(country => {
                      const isSelected = country._id === selectedCountryId;
                      const countryDisplayLabel = `${country.code} ${country.abbreviation}`;
                      return (
                        <CommandItem
                          key={country._id}
                          value={getCountryLabel(country)}
                          onSelect={() => handleCountryChange(country._id)}
                          className='text-white hover:bg-gray-700'
                        >
                          <span className='flex items-center gap-2 w-full'>
                            <span className='w-4 h-4 flex items-center justify-center shrink-0'>
                              {isSelected && (
                                <Check className='h-4 w-4 text-purple-400' />
                              )}
                            </span>
                            <span
                              className={`flex-1 ${
                                isSelected ? 'font-medium' : ''
                              }`}
                            >
                              {countryDisplayLabel}
                            </span>
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {/* Error area - consistent height */}
        <div className='h-5 mt-1' />
      </div>
      <div className='flex-1 flex flex-col'>
        {/* Consistent label area */}
        <div className='h-5 mb-2'>
          <Label className='text-gray-300'>
            {finalLabel}
            {isRequired && <span className='text-red-500'> *</span>}
          </Label>
        </div>
        {/* Input area */}
        <div>
          <Input
            className={`text-white ${error ? 'border-red-500' : validatePhoneInput(value.number).trim() ? 'bg-gray-800/80 border-gray-600' : 'bg-gray-900 border-gray-700'} focus:ring-2 focus:ring-blue-500/30`}
            placeholder={finalPlaceholder}
            value={value.number}
            onChange={e => {
              const sanitized = validatePhoneInput(e.target.value);
              if (sanitized.length <= effectiveMaxLength) {
                onChange({ ...value, number: sanitized });
              }
            }}
            onBlur={onBlur}
            maxLength={effectiveMaxLength}
          />
        </div>
        {/* Error area - consistent height */}
        <div className='h-5 mt-1'>
          {error && <p className='text-red-400 text-xs'>{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PhoneInput;
